import React, { lazy, Suspense } from "react";

import {
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import usePageTracking from "./usePageTracking";

import Header from "./components/Header";
import Footer from "./components/Footer";


// =====================================================
// STATIC PAGES
// =====================================================

const Home = lazy(
  () => import("./pages/Home")
);

const About = lazy(
  () => import("./pages/About")
);

const Contact = lazy(
  () => import("./pages/Contact")
);

const PrivacyPolicy = lazy(
  () => import("./pages/PrivacyPolicy")
);

const Terms = lazy(
  () => import("./pages/Terms")
);

const Sitemap = lazy(
  () => import("./pages/Sitemapsss")
);

const FAQ = lazy(
  () => import("./pages/FAQ")
);

const DMCA = lazy(
  () => import("./pages/DMCA")
);

const ShortsList = lazy(
  () => import("./pages/ShortsList")
);

const ProductGallery = lazy(
  () => import("./ProductGallery")
);

const NotFound = lazy(
  () => import("./pages/NotFound")
);
const JobVacancy = lazy(
  () => import("./pages/JobVacancy")
);
const PortfolioToWebsite = lazy(
  () => import("./pages/PortfolioToWebsite")
);

const PortfolioBuilder = lazy(
  () => import("./pages/PortfolioBuilder")
);

// =====================================================
// ENGLISH CONTENT ARCHITECTURE
// =====================================================
//
// Root:
// /
//
// English:
//
// /en
//
// Category:
//
// /en/:category
//
// Subcategory:
//
// /en/:category/:subcategory
//
// Article:
//
// /en/:category/:subcategory/:slug
//
// Examples:
//
// /en
//
// /en/technology
//
// /en/technology/artificial-intelligence
//
// /en/technology/artificial-intelligence/
// future-of-artificial-intelligence
//
// /en/science/space/
// james-webb-space-telescope
//
// /en/finance/investing/
// best-investment-strategies
//
// =====================================================

const LanguagePage = lazy(
  () => import("./pages/LanguagePage")
);

const OrgCategoryPage = lazy(
  () => import("./pages/OrgCategoryPage")
);

const OrgSubCategoryPage = lazy(
  () => import("./pages/OrgSubCategoryPage")
);

const OrgArticleView = lazy(
  () => import("./pages/OrgArticleView")
);
const PortfolioView = lazy(() => import("./pages/PortfolioView"));

const WeAreEmployees  = lazy(
  () => import("./pages/WeAreEmployees")
);
  
// =====================================================
// LOADING FALLBACK
// =====================================================
//
// Shared fallback for lazy-loaded route components.
//
// aria-live allows assistive technologies to announce
// the loading state.
//
// =====================================================

const PageLoading = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{
      minHeight: "70vh",
      width: "100%",
    }}
    aria-live="polite"
    aria-busy="true"
  >
    <div
      className="text-center"
      role="status"
    >
      <div
        className="spinner-border mb-3"
        aria-hidden="true"
      />

      <div
        style={{
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        Loading...
      </div>
    </div>
  </div>
);

// =====================================================
// APP
// =====================================================

function App() {

  const location = useLocation();

  // ===================================================
  // GOOGLE ANALYTICS / PAGE TRACKING
  // ===================================================
  //
  // Tracking remains separate from SEO logic.
  //
  // This tracks navigation without affecting:
  //
  // - canonical URLs
  // - metadata
  // - structured data
  // - sitemap generation
  // - article rendering
  //
  // ===================================================

  usePageTracking(location.pathname);

  return (
    <div className="App">

      {/* =================================================
          GLOBAL HEADER
      ================================================= */}

      <Header />

      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <main
        className="main-content"
        id="main-content"
      >

        <Suspense
          fallback={<PageLoading />}
        >

          <Routes>

            {/* =================================================
                HOME
            ================================================= */}

            <Route
              path="/"
              element={<Home />}
            />

            {/* =================================================
                STATIC INFORMATION PAGES
            ================================================= */}

            <Route
              path="/about"
              element={<About />}
            />

            <Route
              path="/contact"
              element={<Contact />}
            />

            <Route
              path="/privacy-policy"
              element={<PrivacyPolicy />}
            />

            <Route
              path="/terms"
              element={<Terms />}
            />

            <Route
              path="/faq"
              element={<FAQ />}
            />

            <Route
              path="/dmca"
              element={<DMCA />}
            />

            <Route
              path="/sitemap"
              element={<Sitemap />}
            />

            <Route
              path="/shorts"
              element={<ShortsList />}
            />

            <Route
              path="/products"
              element={<ProductGallery />}
            />

            <Route
              path="/job_vacancy"
              element={< JobVacancy />}
            />

            <Route
              path="/portfolio_to_website"
              element={< PortfolioToWebsite />}
            />

            <Route
              path="/portfolio_builder"
              element={< PortfolioBuilder />}
            />

            <Route
  path="/we_are_employees"
  element={<WeAreEmployees />}
/>

<Route
  path="/portfolio/:countryCode/:userId"
  element={<PortfolioView />}
/>

            {/* ================================================= PortfolioBuilder
                ENGLISH CONTENT SYSTEM
            ================================================= */}

            {/* -----------------------------------------------
                English Homepage

                /en
            ------------------------------------------------ */}

            <Route
              path="/en"
              element={< LanguagePage />}
            />

            {/* -----------------------------------------------
                English Category

                /en/:category

                Example:
                /en/technology
            ------------------------------------------------ */}

            <Route
              path="/en/:category"
              element={<OrgCategoryPage />}
            />

            {/* -----------------------------------------------
                English Subcategory

                /en/:category/:subcategory

                Example:
                /en/technology/artificial-intelligence
            ------------------------------------------------ */}

            <Route
              path="/en/:category/:subcategory"
              element={<OrgSubCategoryPage />}
            />

            {/* -----------------------------------------------
                English Article

                /en/:category/:subcategory/:slug

                Example:
                /en/technology/artificial-intelligence/
                future-of-artificial-intelligence
            ------------------------------------------------ */}

            <Route
              path="/en/:category/:subcategory/:slug"
              element={<OrgArticleView />}
            />

            {/* =================================================
                CATCH-ALL / 404
            ================================================= */}

            <Route
              path="*"
              element={<NotFound />}
            />

          </Routes>

        </Suspense>

      </main>

      {/* =================================================
          GLOBAL FOOTER
      ================================================= */}

      <Footer />

    </div>
  );
}

export default App;

